/* eslint-disable react-refresh/only-export-components */
import { useEffect, useRef, useState } from "react";

type Subscriber = (message: string) => void;

const subscribers = new Set<Subscriber>();
const pendingQueue: string[] = [];

export function announce(message: string) {
  if (!message) return;
  if (subscribers.size === 0) {
    pendingQueue.push(message);
    return;
  }
  subscribers.forEach((cb) => cb(message));
}

export function useLiveAnnouncer() {
  return { announce };
}

export function LiveAnnouncer() {
  const [message, setMessage] = useState("");
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler: Subscriber = (msg) => {
      if (clearTimer.current) clearTimeout(clearTimer.current);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      setMessage("");
      resetTimer.current = setTimeout(() => {
        setMessage(msg);
        clearTimer.current = setTimeout(() => setMessage(""), 1000);
      }, 50);
    };

    subscribers.add(handler);

    if (pendingQueue.length > 0) {
      const drained = pendingQueue.splice(0, pendingQueue.length);
      drained.forEach((msg) => handler(msg));
    }

    return () => {
      subscribers.delete(handler);
      if (clearTimer.current) clearTimeout(clearTimer.current);
      if (resetTimer.current) clearTimeout(resetTimer.current);
    };
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
}
